/**
 * {@link DataTable} for programs.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { ProgramRow } from './ProgramRow';
import { ProgramCard } from './ProgramCard';
import { ProgramFiltersContent } from './ProgramFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderProgramDetails } from './renderProgramDetails';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { getPrograms, deleteProgram, exportProgramsExcel } from '@/services/programs.service';
import { cn } from '@/lib/utils';
import { useMode } from '@/contexts/ModeContext';
import { getProgramTypesForMode } from '@/utils/program-mode-utils';
import {
  ALL_FILTER_GROUP_IDS,
  DEFAULT_PROGRAM_FILTERS,
  FILTER_GROUP_LABELS,
  type FilterGroupId,
  type ProgramFilters,
  type ProgramType,
} from './program-configs';
import { hasProgramActiveFilters, toProgramListParams } from './program-table-utils';
import type { ViewMode } from '@/types/data-table.types';
import type { Program } from '@/types/program.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link ProgramsDataTable}. */
interface ProgramsDataTableProps {
  programs?: Program[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditProgram?: (id: string) => void;
  enableFilters?: boolean;
  defaultFilters?: ProgramFilters;
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean; canExportCsv?: boolean };
  showCreateButton?: boolean;
  onCreateProgram?: () => void;
}

/** Programs list / grid with filters, details, delete, and Excel export. */
export const ProgramsDataTable: React.FC<ProgramsDataTableProps> = ({
  programs: externalPrograms,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditProgram,
  enableFilters = true,
  showCreateButton = false,
  onCreateProgram,
  defaultFilters = DEFAULT_PROGRAM_FILTERS,
  layoutWidth = 'full',
  isSection = false,
  pageActions,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;

  const { mode } = useMode();
  const modeTypes = getProgramTypesForMode(mode ?? 'in_person');

  const dt = useDataTable<ProgramFilters, FilterGroupId>({
    tableName: 'programs',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['programs', dt.currentPage, dt.itemsPerPage, dt.filters, mode],
    queryFn: async () =>
      getPrograms(toProgramListParams(dt.filters, dt.currentPage, dt.itemsPerPage, modeTypes)),
    enabled: !externalPrograms,
  });

  const data = React.useMemo(
    () => externalPrograms ?? (apiResponse?.data || []),
    [externalPrograms, apiResponse?.data],
  );
  const totalItems = apiResponse?.total ?? data.length;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = React.useMemo(
    () => (externalPrograms ? data.length === 0 : !isLoading && totalItems === 0),
    [externalPrograms, data.length, isLoading, totalItems],
  );

  /** Excel export of the current filter set (blocked when the list is empty). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportProgramsExcel();
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [isEmptyForExport]);

  const {
    data: expandedProgramData,
    isLoading: isLoadingExpandedProgram,
    isError: isExpandedProgramError,
    error: expandedProgramError,
  } = useQuery({
    queryKey: ['programs', expandedRowId],
    queryFn: async () => {
      if (!expandedRowId) return null;
      const { getProgram } = await import('@/services/programs.service');
      return await getProgram(expandedRowId);
    },
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedProgramError,
    error: expandedProgramError,
    catalogKey: 'programNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hasActiveFilters = React.useMemo(() => hasProgramActiveFilters(dt.filters), [dt.filters]);

  const deleteMutation = useMutation({
    mutationFn: deleteProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['programs'] });
      dt.setDeleteId(null);
      toast.success('Programme supprimé avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const programToDelete = React.useMemo(
    () => data.find((p) => p.id === dt.deleteId),
    [data, dt.deleteId],
  );

  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleTypeFilter = React.useCallback(
    (type: ProgramType) => {
      dt.setFilters((prev) => ({
        ...prev,
        types: prev.types.includes(type)
          ? prev.types.filter((t) => t !== type)
          : [...prev.types, type],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <ProgramFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleStatus={toggleStatusFilter}
        onToggleType={toggleTypeFilter}
      />
    ),
    [dt.filters, dt.allowedFilterIds, dt.visibleFilterIds, toggleStatusFilter, toggleTypeFilter],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const expandedProgram = expandedProgramData ?? null;

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="programmes"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={5}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => dt.setShowFilters((prev) => !prev)}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des programmes en Excel'
              }
            />
          )}
          {showCreateButton && onCreateProgram && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un programme"
              onClick={() => {
                onCloseDetails?.();
                onCreateProgram();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={dt.clearFilters}
            resultCount={totalItems}
            resultLabel="programme(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Code</th>
          <th>Nom</th>
          <th>Période</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(program) => (
        <ProgramRow
          program={program}
          onView={onToggleRow ? () => onToggleRow(program.id) : undefined}
          onEdit={onEditProgram}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(program) => (
        <DetailsViewContent>{renderProgramDetails(program)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(program) => (
        <ProgramCard
          program={program}
          onView={onToggleRow ? () => onToggleRow(program.id) : undefined}
          onEdit={onEditProgram}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={FileText}
      emptyText="Aucun programme trouvé"
      title="Programmes"
      sidebarMenuId="menu-programs"
      isSection={isSection}
      expandedItem={expandedProgram}
      isExpandedDetailLoading={isLoadingExpandedProgram}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(program) => (
        <DetailsViewContent>{renderProgramDetails(program)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={programToDelete?.name}
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
